import React, { useState } from 'react';
import { Crown, RefreshCw } from 'lucide-react';
import { message } from 'antd';
import { UserInfo } from '@/types/membership';
import api from '@/api/axios';

interface UserInfoHeaderProps {
  userInfo?: UserInfo;
  onRefresh?: () => void; // 添加刷新回调
}

export default function UserInfoHeader({ userInfo, onRefresh }: UserInfoHeaderProps) {
  const [refreshing, setRefreshing] = useState(false);

  // 刷新余额 - 检查待支付订单
  const handleRefreshBalance = async () => {
    setRefreshing(true);
    try {
      const response = await api.post(
        '/api/check_pending_orders'
      );
      const result = response.data;

      // 无论 success 是 true 还是 false，都处理余额更新
      // 这符合 "Never break userspace" 原则
      if (result.current_balance !== undefined) {
        // 调用父组件的刷新方法来更新用户信息
        onRefresh?.();
      }

      // 根据结果显示不同的消息
      if (result.success) {
        if (result.updated_count > 0) {
          message.success(`检查完成，更新了 ${result.updated_count} 个订单，余额已刷新`);
        } else {
          message.info('检查完成，没有需要更新的订单');
        }
      } else {
        // 即使检查失败，如果有余额信息也要显示
        if (result.current_balance !== undefined) {
          message.warning(`订单检查遇到问题，但余额已更新: ${result.message || '请稍后重试'}`);
        } else {
          message.error(`检查订单失败: ${result.message || '请稍后重试'}`);
        }
      }
    } catch (error: any) {
      console.error('刷新余额失败:', error);
      const errorMsg = error.response?.data?.detail || '网络错误，请稍后重试';
      message.error(errorMsg);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="user-member-stats">
      {/* 账户余额 */}
      <div className="user-member-stat">
        <div className="flex items-center justify-center mb-2">
          <svg className="w-8 h-8 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          <h3 className="text-lg font-semibold text-white">账户余额</h3>
          {/* 刷新按钮 */}
          <button
            onClick={handleRefreshBalance}
            disabled={refreshing}
            className="user-member-refresh"
            title="检查待支付订单并刷新余额"
          >
            <RefreshCw
              className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
        <p className="text-2xl font-bold text-white">
          ¥{userInfo?.balance?.toFixed(2) || '0.00'}
        </p>
      </div>

      {/* 会员状态 */}
      <div className="user-member-stat">
        <div className="flex items-center justify-center mb-2">
          <Crown className="w-8 h-8 mr-2 text-white" />
          <h3 className="text-lg font-semibold text-white">会员状态</h3>
        </div>
        <p className="text-2xl font-bold text-white">
          {userInfo?.membership_expiry ? '已开通' : '未开通'}
        </p>
      </div>

      {/* 到期时间 */}
      <div className="user-member-stat">
        <div className="flex items-center justify-center mb-2">
          <svg className="w-8 h-8 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-2 13a2 2 0 002 2h8a2 2 0 002-2L16 7" />
          </svg>
          <h3 className="text-lg font-semibold text-white">到期时间</h3>
        </div>
        <p className="text-2xl font-bold text-white">
          {userInfo?.membership_expiry ? new Date(userInfo.membership_expiry).toLocaleDateString() : '未开通'}
        </p>
      </div>
    </div>
  );
}
